import React from 'react';
import PropTypes from 'prop-types';
import Form from 'react-form-controlled';
import MessageDefinitions from '../i18n/MessageDefinitions';
import { FormattedMessage, intlShape } from 'react-intl';
import FontAwesome from 'react-fontawesome';
import {connectToStores} from "fluxible-addons-react";
import {changeAppointmentFlow, timeSlotSelected} from "../actions/ContactMeActionCreators";
import BigCalendar from 'react-big-calendar';
import moment from 'moment';
import 'moment-timezone';
import Screens from "../constants/Screens";
import {LocationShape} from "../../common/react/Shapes";

BigCalendar.momentLocalizer(moment);

@connectToStores(['ContactMeStore'], (context, props) => {
  const ContactMeStore = context.getStore('ContactMeStore');
  return {
    selectedLocation: ContactMeStore.getSelectedLocation(),
    selectedTime: ContactMeStore.getSelectedTime(),
    currentLead: ContactMeStore.getLead()
  };
})
class AppointmentCalendarForm extends React.Component {
  static propTypes = {
    selectedLocation: LocationShape,
    selectedTime: PropTypes.object,
    currentLead: PropTypes.object
  };

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    intl: intlShape
  };

  constructor(props) {
    super(props);
    this.state = {
      processing: false,
      availableTimes: [],
      loading: true,
      showTheErrors: false,
      calendarView: 'week'
    };

    this.queryTimeSlots(new Date());
    window.ignite.getAnswerService().then(answerService => {
      this.answerService = answerService;
    });
  }

  componentDidMount() {
    window.addEventListener("resize", this.resize.bind(this));
    this.resize();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resize.bind(this));
  }

  resize() {
    this.setState({
      calendarView: window.innerWidth <= 600 ? 'day' : 'week'
    });
  }

  formSubmitted = async () => {
    if (!this.props.selectedTime.time) {
      this.setState({
        showTheErrors: true
      })
    } else {
      this.setState({
        processing: true
      });

      const getResourceId = async resourceIds => {
        let matchingId = false;
        if (window.ignite.employeeLoginService) {
          const employee = await window.ignite.employeeLoginService.getLoggedInEmployee();
          const employeeId = employee.employeeId || "";
          matchingId = resourceIds.find(id => id.toLowerCase() === employeeId.toLowerCase())
        }
        return matchingId || resourceIds[Math.floor(Math.random() * resourceIds.length)];
      };

      let nextScreen = Screens.FAIL;
      const resourceIds = this.props.selectedTime.resourceExternalIds;
      const resourceId = resourceIds ? await getResourceId(resourceIds) : 0;

      window.ignite.appointmentService.bookAppointment({
        duration: this.props.selectedTime.length,
        locationId: this.props.selectedLocation.id,
        resourceId,
        time: this.props.selectedTime.time,
        client: {
          firstName: this.props.currentLead.firstName,
          lastName: this.props.currentLead.lastName,
          email: this.props.currentLead.email,
          phone: this.props.currentLead.phone
        }
      }).then(result => {
        if (result.status) {
          nextScreen = Screens.SUCCESS;
          this.answerService.saveAnswer({
            appkey: 'appointmentId',
            value: result.id,
            timestamp: new Date()
          });
          this.answerService.saveAnswer({
            appkey: 'appointmentTime',
            value: this.props.selectedTime.start.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                year: 'numeric',
                day: 'numeric'
              }) +
              '-' + this.props.selectedTime.start.toLocaleTimeString('en-US', {hour: 'numeric', minute: 'numeric'}) +
              ' | ' + this.props.selectedLocation.name,
            timestamp: new Date()
          });
          this.answerService.saveAnswer({
            appkey: 'makeAppointmentClicked',
            value: 'completed',
            timestamp: new Date()
          });
        }
        this.context.executeAction(changeAppointmentFlow, {nextScreen});
      }, error => {
        console.log(error);
        this.context.executeAction(changeAppointmentFlow, {nextScreen});
      });
    }
  };

  updateTimeSlotSelection = timeSlot => {
    this.setState({
      showTheErrors: false
    });
    this.context.executeAction(timeSlotSelected, timeSlot);
  };

  queryTimeSlots = (date) => {
    this.setState({
      loading: true
    });
    const start = moment(date).startOf('week');
    const end = moment(date).endOf('week');
    window.ignite.appointmentService.getTimeSlots({
      locationId: this.props.selectedLocation.id,
      startDate: start.toISOString(),
      endDate: end.toISOString()
    }).then(availableTimes => {
      availableTimes.forEach( timeSlot => {
        const momentDateTime = moment(timeSlot.time).tz(this.props.selectedLocation.timeZone);
        timeSlot.start = this._convertMomentToDateForCalendar(momentDateTime);
        timeSlot.end = this._convertMomentToDateForCalendar(momentDateTime.clone().add(parseInt(timeSlot.length), "minutes"));
      });
      this.setState({
        availableTimes,
        loading: false
      });
    });
  };

  _convertMomentToDateForCalendar = (momentDateTime) => {
    return new Date(momentDateTime.year(), momentDateTime.month(), momentDateTime.date(), momentDateTime.hour(), momentDateTime.minute(), 0);
  };

  render() {
    const { selectedLocation, selectedTime } = this.props;
    const { processing, availableTimes, loading, showTheErrors, calendarView } = this.state;

    const minTime = new Date();
    const maxTime = new Date();
    minTime.setHours(7,0,0); //7:00am
    maxTime.setHours(20,0,0); //8:00pm

    const dayFormat = { weekday: 'long', month: 'long',  year: 'numeric', day: 'numeric' };
    const timeFormat = { hour: 'numeric', minute: 'numeric' };

    const selectedLocationString =
      selectedLocation && selectedLocation.id
        ?
        <div className="selectedLocationString">Selected Location:
          <span className="name">{selectedLocation.name} </span>
          <span className="address">{selectedLocation.address}, </span>
          <span className="cityState">{selectedLocation.city}, {selectedLocation.state} {selectedLocation.postalCode}</span>
        </div>
        : null;

    const selectedTimeString =
      selectedTime && selectedTime.time
        ?
        <div className="selectedTimeString"> Selected Time:
          <span className="startTime">{selectedTime.start.toLocaleDateString('en-US', dayFormat)}, </span>
          <span className="timeString">{selectedTime.start.toLocaleTimeString('en-US', timeFormat)}</span>
        </div>
        :
        null;

    const buttonMessage =
      processing
        ? <FontAwesome name="refresh" spin/>
        : <FormattedMessage {...MessageDefinitions.confirmAppointment} />;

    const errorString =
      showTheErrors
        ? <div className="errorString"><FormattedMessage {...MessageDefinitions.appointmentTimeSelectError} /></div>
        : null;


    return (
      <Form className="form-container" onSubmit={this.formSubmitted}>
        <div className="form-body calendar-section">
          {loading ? <div className="loading-section"><FontAwesome name="spinner" spin/></div> : null }
          <h5 className="appointment-header"><FormattedMessage {...MessageDefinitions.appointmentTimeSelectMessage} /></h5>
          {selectedLocationString}
          {selectedTimeString}
          <BigCalendar
            selectable
            events={availableTimes}
            view={calendarView}
            views={{ month: false, week: true, day: true, agenda: false }}
            defaultDate={new Date()}
            min = {minTime}
            max = {maxTime}
            onSelectEvent={timeSelection => this.updateTimeSlotSelection(timeSelection)}
            onNavigate={date => this.queryTimeSlots(date)}
          />
          {errorString}
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary save" disabled={processing || showTheErrors }>
            {buttonMessage}
          </button>
        </div>
      </Form>
    );
  }
}

export default AppointmentCalendarForm;



/** WEBPACK FOOTER **
 ** ./src/main/js/contactMe/components/AppointmentCalendarForm.js
 **/